import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import {
  Button,
  Container,
  Link,
  styled,
  Typography,
  Grid,
  Box,
  Divider,
} from '@mui/material'
import React, { useRef } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import config from '../../config'
import { activeStepState, formDataState } from '../helpers'
import Layout from './layout.component'
import PropTypes from 'prop-types'
import AppStoreButton from './appstore.component'
import { QRCodeCanvas } from 'qrcode.react'

const PREFIX = 'SuccessError'

export const classes = {
  containerTop: `${PREFIX}-container-top`,
  containerMid: `${PREFIX}-container-mid`,
  containerBottom: `${PREFIX}-container-bottom`,
  label: `${PREFIX}-label`,
  marginTop: `${PREFIX}-margin-top`,
  registerAnother: `${PREFIX}-register-another`,
  registerAnother2: `${PREFIX}-register-another2`,
  appstoreContainer: `${PREFIX}-appstore-container`,
  containerESim: `${PREFIX}-container-esim`,
  boxQR: `${PREFIX}-box-qr`,
  containerManual: `${PREFIX}-container-manual`,
  btnCopy: `${PREFIX}-btn-copy`,
}

export const styles = ({ theme }) => ({
  [`& .${classes.containerTop}`]: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
  },
  [`& .${classes.containerMid}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: theme.spacing(5, 0, 3, 0),
    padding: theme.spacing(0, 4),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 20),
    },
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 30),
    },
  },
  [`& .${classes.containerBottom}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 30),
    },
  },
  [`& .${classes.containerBottom} > h6 > span`]: {
    fontWeight: '700',
    fontSize: theme.spacing(2.3),
  },
  [`& .${classes.label}`]: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'inline-flex',
      alignItems: 'center',
    },
  },
  [`& .${classes.marginTop}`]: {
    marginTop: '30px',
  },
  [`& .${classes.registerAnother}`]: {
    display: 'flex',
    padding: theme.spacing(1, 0),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  [`& .${classes.registerAnother2}`]: {
    display: 'block',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  [`& .${classes.appstoreContainer}`]: {
    [theme.breakpoints.up('md')]: {
      width: '65%',
    },
    [theme.breakpoints.up('lg')]: {
      width: '50%',
    },
  },
  [`& .${classes.containerESim}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(0, 15),
      '& .leftContainer': {
        padding: '0 40px', // Adjust the padding value as needed
      },
    },
    '& button': {
      marginRight: '0px',
    },
  },
  [`& .${classes.boxQR}`]: {
    margin: '20px 0px',
  },
  [`& .${classes.containerManual}`]: {
    textAlign: 'left',
    marginLeft: '10px',
    [theme.breakpoints.up('md')]: {
      ['.MuiBox-root']: {
        marginLeft: '20px',
      },
    },
  },
  [`& .${classes.btnCopy}`]: {
    margin: '0px',
  },
})

const StyledDiv = styled('div')(styles)

SuccessError.propTypes = {
  canRetry: PropTypes.bool.isRequired,
  handleRetry: PropTypes.func.isRequired,
  onRegisterAnother: PropTypes.func,
}

function SuccessError({ canRetry, handleRetry, onRegisterAnother }) {
  const formData = useRecoilValue(formDataState)
  const setActiveStep = useSetRecoilState(activeStepState)
  const qrRef = useRef(null)

  const getErrorContent = () => {
    let errorType = formData.errorType
    if (canRetry && errorType !== -1) {
      errorType = 0
    }
    switch (errorType) {
      case -1:
        return 'For passport user, please proceed to any M1 shop for registration.'
      case 1:
        return 'You have exceeded the maximum of 3 global prepaid lines. Please de-register one of your prepaid lines and try again.'
      case 2:
        return (
          <>
            Need help? Chat with us via{' '}
            <Link href="https://www.m1.com.sg" target="_blank" rel="noopener">
              m1.com.sg
            </Link>{' '}
            from 9:00 AM to 6:00 PM. Please prepare your mobile number and the
            18-digit SIM card serial number found on the back of your SIM card.
          </>
        )
      case 3:
        return (
          <div>
            You have submitted the following:
            <br />
            - Invalid port-in number and/or
            <br />
            - ID mismatch
            <br />
            <br />
            Please resubmit with the correct information
          </div>
        )
      case 4:
        return 'We are unable to verify your global prepaid lines limit at the moment. Please try again at a later time.'
      default:
        return "Something went wrong. Let's try one more time!"
    }
  }

  const getErrorTitle = (errorTitleType) => {
    switch (errorTitleType) {
      case 1:
        return 'SIM Limit Reached'
      case 2:
        return 'Registration Error'
      case 3:
        return 'Port-in Error'
      default:
        return 'Oops'
    }
  }

  const getErrorIcon = (errorType) => {
    switch (errorType) {
      case 1:
      case 2:
      case 3:
        return <img src={config.PUBLIC_URL + '/error_2.svg'} alt="icon" />
      default:
        return <img src={config.PUBLIC_URL + '/error.svg'} alt="icon" />
    }
  }

  const handleClick = (errorType) => {
    if ((!canRetry && errorType !== 3) || (canRetry && errorType === -1)) {
      {
        window.location.href = '/'
      }
    } else {
      if (!canRetry && errorType === 3) {
        setActiveStep(0)
      } else {
        handleRetry()
      }
    }
  }

  const getButtonText = (canRetry, errorType) => {
    if ((!canRetry && errorType !== 3) || (canRetry && errorType === -1)) {
      return 'Home'
    } else {
      if (!canRetry && errorType === 3) {
        return 'Edit Information'
      }

      return 'Try again'
    }
  }

  const downloadQRCode = () => {
    const canvas = qrRef.current.querySelector('canvas')
    const url = canvas.toDataURL('image/png')
    const link = document.createElement('a')
    link.href = url
    link.download = 'qrcode.png'
    link.click()
  }

  const copyText = (value) => {
    navigator.clipboard.writeText(value)
  }

  return (
    <Layout
      showMobileMenu
      content={
        <StyledDiv>
          <Container className={classes.containerTop}>
            {formData.isSuccess && (
              <Grid container>
                <Grid item xs={6}>
                  {!formData.esim && (
                    <Typography variant="h4" className={classes.label}>
                      Activation Successful
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6} sx={{ textAlign: 'right' }}>
                  <Link
                    component={Button}
                    startIcon={<OpenInNewOutlinedIcon />}
                    onClick={onRegisterAnother}
                  >
                    Register Another Number
                  </Link>
                </Grid>
              </Grid>
            )}
          </Container>
          <Container className={classes.containerMid}>
            {!formData.isSuccess ? (
              getErrorIcon(canRetry ? 0 : formData.errorType)
            ) : (
              <img src={config.PUBLIC_URL + '/success.svg'} alt="icon" />
            )}
            <Typography variant="h2" color="primary" sx={{ m: '10px 0px' }}>
              {!formData.isSuccess
                ? getErrorTitle(canRetry ? 0 : formData.errorTitleType)
                : 'Success!'}
            </Typography>
            <Typography variant="subtitle2" textAlign="left">
              {!formData.isSuccess
                ? getErrorContent()
                : 'Welcome to the M1 family!'}
            </Typography>
            {!formData.isSuccess && (
              <Button
                variant="contained"
                color="primary"
                disabled={false}
                onClick={() => handleClick(formData.errorType)}
                sx={{ mr: false, width: { xs: '90%', md: '50%' } }}
              >
                {getButtonText(canRetry, formData.errorType)}
              </Button>
            )}
          </Container>
          <Container className={classes.containerBottom}>
            {formData.isSuccess && (
              <>
                {formData && formData.portinMsisdn && (
                  <>
                    <Typography variant="subtitle2">
                      Your existing number <span>{formData.portinMsisdn}</span>{' '}
                      is now pending for port-in. We will inform you via SMS
                      once it is ready for use. <br />
                      <br />
                      This will be your temporary number till then
                      <br />
                    </Typography>
                    <Typography variant="h2">{formData.msisdn}</Typography>
                    <br />
                    <Typography variant="subtitle2">
                      Please note that you cannot make or receive calls, send or
                      receive SMS, or log in to the M1 Prepaid App using your
                      temporary number. Data remains available during port-in.
                    </Typography>
                  </>
                )}
                {formData && !formData.portinMsisdn && !formData.esim && (
                  <>
                    <Typography variant="h2">{formData.msisdn}</Typography>
                    <Typography variant="subtitle2">
                      is activated and ready for use.
                    </Typography>
                    <Link
                      component={Button}
                      startIcon={<OpenInNewOutlinedIcon />}
                      onClick={onRegisterAnother}
                      className={classes.registerAnother}
                    >
                      Register Another Number
                    </Link>
                    <br />
                  </>
                )}
              </>
            )}
            {formData && formData.isSuccess && formData.isEsim ? (
              <Container>
                <Typography variant="subtitle2">
                  <b>
                    Can't view your eSIM QR code or details? Chat with us via{' '}
                    <Link
                      href="https://www.m1.com.sg"
                      target="_blank"
                      rel="noopener"
                    >
                      m1.com.sg
                    </Link>{' '}
                    from 9:00 AM to 6:00 PM for further assistance.
                  </b>
                </Typography>
                <br />
              </Container>
            ) : null}
          </Container>
          {formData &&
          formData.isSuccess &&
          formData.isEsim &&
          formData.esim ? (
            <Container className={classes.containerESim}>
              <Grid container>
                <Grid item xs={12} md={6} className="leftContainer">
                  <Typography variant="subtitle">
                    <b>Scan QR code to install your eSIM</b>
                  </Typography>
                  <Box ref={qrRef} className={classes.boxQR}>
                    {
                      <QRCodeCanvas
                        value={formData.esim?.MATCHING_ID}
                        size={200}
                      />
                    }
                  </Box>
                  <Typography
                    sx={{ fontSize: '12.5px!important', color: '#A9A9A9' }}
                  >
                    Download or take a screenshot if you would like to install
                    the eSIM later.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={downloadQRCode}
                  >
                    Download
                  </Button>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Container className={classes.containerManual}>
                    <Typography variant="subtitle">
                      <b>How do I install the eSIM manually?</b>
                    </Typography>
                    <br />
                    <br />
                    <Typography fontWeight="700" sx={{ marginBottom: '10px' }}>
                      For iOS Devices{' '}
                    </Typography>
                    <Typography>Copy and paste the following: </Typography>
                    <br />
                    <Box>
                      <Typography fontWeight="700">SM-DP+ Address</Typography>
                      <Grid container alignItems={'center'}>
                        <Grid item xs={8} sx={{ wordBreak: 'break-all' }}>
                          <Typography>
                            {formData.esim?.MATCHING_ID.split('$')[1]}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Button
                            variant="text"
                            size="small"
                            className={classes.btnCopy}
                            onClick={() =>
                              copyText(formData.esim?.MATCHING_ID.split('$')[1])
                            }
                          >
                            Copy
                          </Button>
                        </Grid>
                      </Grid>{' '}
                      <br />
                      <Typography fontWeight="700">Activation Code</Typography>
                      <Grid container alignItems={'center'}>
                        <Grid item xs={8} sx={{ wordBreak: 'break-all' }}>
                          <Typography>
                            {formData.esim?.MATCHING_ID.split('$')[2]}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Button
                            variant="text"
                            size="small"
                            className={classes.btnCopy}
                            onClick={() =>
                              copyText(formData.esim?.MATCHING_ID.split('$')[2])
                            }
                          >
                            Copy
                          </Button>
                        </Grid>
                      </Grid>{' '}
                    </Box>

                    <br />
                    <Divider />
                    <Typography fontWeight="700" sx={{ marginBottom: '10px' }}>
                      For Android Devices{' '}
                    </Typography>
                    <Typography>Copy and paste the following: </Typography>
                    <br />
                    <Box>
                      <Typography fontWeight="700">Activation Code</Typography>
                      <Grid container alignItems={'center'}>
                        <Grid item xs={8} sx={{ wordBreak: 'break-all' }}>
                          <Typography>
                            {formData.esim?.MATCHING_ID.replace('LPA:', '')}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Button
                            variant="text"
                            size="small"
                            className={classes.btnCopy}
                            onClick={() =>
                              copyText(
                                formData.esim?.MATCHING_ID.replace('LPA:', '')
                              )
                            }
                          >
                            Copy
                          </Button>
                        </Grid>
                      </Grid>{' '}
                    </Box>
                    <br />
                  </Container>
                </Grid>
                <Grid item xs={12} className={classes.registerAnother2}>
                  <Link
                    component={Button}
                    startIcon={<OpenInNewOutlinedIcon />}
                    onClick={onRegisterAnother}
                  >
                    Register Another Number
                  </Link>
                </Grid>
              </Grid>
            </Container>
          ) : null}

          {(!canRetry || formData.isSuccess) && (
            <Container className={classes.containerBottom}>
              <Typography
                variant="subtitle2"
                align="center"
                fontWeight="600"
                className={classes.marginTop}
              >
                Download M1 Prepaid App to manage your account <br />
                <br />
              </Typography>
              <AppStoreButton />
            </Container>
          )}
        </StyledDiv>
      }
    />
  )
}

export default SuccessError
